import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import type {
  ChecklistOfferGroup,
  EstimateLine,
  EstimateLineConfidence,
  EstimateLineReviewStatus,
  OfferRow,
  Room,
  ScopeId,
  SectionHeader,
  VatMode,
} from "@/types/mvpEstimate";
import { isEstimateLineRow, isSectionHeader } from "@/types/mvpEstimate";
import { normalizeWizardScopes } from "@/hooks/use-mvp-persistence";
import { supabase } from "@/integrations/supabase/client";
import { estimateLineElementId } from "@/utils/estimateReviewSummary";
import {
  effectiveLineVatRate,
  estimateVatSummary,
  lineFinalPrice,
  lineGrossPrice,
  lineMarginAmount,
  lineVatAmount,
  roundMoney,
  vatRateFromMode,
} from "@/utils/mvpEstimateMath";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCenter,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  ChevronDown,
  GripVertical,
  Loader2,
  Maximize2,
  MoreHorizontal,
  Pencil,
  Plus,
  RotateCcw,
  Scissors,
  Trash2,
} from "lucide-react";
import {
  useEffect,
  Fragment,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type Dispatch,
  type KeyboardEvent,
  type ReactNode,
  type SetStateAction,
} from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";

export type AddRowTemplateOption = {
  id: string;
  name: string;
  unit: string;
  category?: string;
  laborUnitPrice: number;
  materialUnitPrice: number;
  source: "user_rate" | "default";
};

type Props = {
  lines: OfferRow[];
  onChange: Dispatch<SetStateAction<OfferRow[]>>;
  onAddRow: () => void;
  onAddRowFromTemplate: (template: AddRowTemplateOption) => void;
  loadAddRowTemplates: () => Promise<AddRowTemplateOption[]>;
  onRestoreRowPrice?: (lineId: string) => void;
  /** Tryb skupienia na tabeli — sterowany przez stronę kosztorysu. */
  focusMode?: boolean;
  onFocusModeChange?: (next: boolean) => void;
  /** Układ Workshop Modern — karty sekcji zamiast szerokiej tabeli. */
  workshopLayout?: boolean;
};

const SECTION_LABELS: Record<ChecklistOfferGroup, string> = {
  malowanie: "Malowanie",
  podlogi: "Podłogi",
  hydraulika: "Hydraulika",
  elektryka: "Elektryka",
  montaze_naprawy: "Montaże i naprawy",
};

const ROMAN = ["I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X"];

function groupLinesForWorkshop(lines: EstimateLine[]) {
  const groups = new Map<string, EstimateLine[]>();
  const order: string[] = [];
  for (const line of lines) {
    const key = line.checklistOfferGroup ?? "__other__";
    if (!groups.has(key)) {
      groups.set(key, []);
      order.push(key);
    }
    groups.get(key)!.push(line);
  }
  return order.map((key, idx) => ({
    key,
    roman: ROMAN[idx] ?? String(idx + 1),
    title:
      key === "__other__"
        ? "Pozostałe pozycje"
        : SECTION_LABELS[key as ChecklistOfferGroup] ?? key,
    lines: groups.get(key)!,
    sectionTotal: groups.get(key)!.reduce((s, l) => s + lineGrossPrice(l), 0),
  }));
}

function laborTotal(line: EstimateLine): number {
  return roundMoney(line.quantity * line.laborUnitPrice);
}

function materialTotal(line: EstimateLine): number {
  return roundMoney(line.quantity * line.materialUnitPrice);
}

/** Netto po marży za jednostkę (wartość pozycji netto / ilość). */
function lineUnitNetAfterMargin(line: EstimateLine): number {
  if (line.quantity <= 0) return 0;
  return roundMoney(lineFinalPrice(line) / line.quantity);
}

/** Suma robocizny i materiału w PLN (przed marżą) — do widoku łączonego. */
function lineLaborMaterialSumPln(line: EstimateLine): number {
  return roundMoney(laborTotal(line) + materialTotal(line));
}

/**
 * Ustawia sumę rob.+mat. (PLN przed marżą) przez jedną aktualizację `laborUnitPrice` / `materialUnitPrice`.
 * Model: bez nowych pól — tylko mapowanie na istniejące stawki.
 */
function applyCombinedLaborMaterialTotal(
  line: EstimateLine,
  newTotalPln: number,
  patchLine: (id: string, patch: Partial<EstimateLine>) => void
) {
  const T = roundMoney(newTotalPln);
  const lab = laborTotal(line);
  const mat = materialTotal(line);
  const q = line.quantity > 0 ? line.quantity : 1;

  let newLabTotal: number;
  let newMatTotal: number;

  if (lab > 0 && mat > 0) {
    const sum = lab + mat;
    const shareL = lab / sum;
    newLabTotal = roundMoney(T * shareL);
    newMatTotal = roundMoney(T - newLabTotal);
  } else if (lab > 0 && mat <= 0) {
    newLabTotal = T;
    newMatTotal = 0;
  } else if (lab <= 0 && mat > 0) {
    newLabTotal = 0;
    newMatTotal = T;
  } else {
    newLabTotal = T;
    newMatTotal = 0;
  }

  patchLine(line.id, {
    laborUnitPrice: roundMoney(newLabTotal / q),
    materialUnitPrice: roundMoney(newMatTotal / q),
  });
}

type CostTableView = "detailed" | "combined";

/** Parsuje wpis liczbowy; puste → 0; ujemne obcinamy do 0 (MVP). */
function parseNonNegativeMoneyInput(raw: string): number {
  const t = raw.trim().replace(/\s/g, "").replace(",", ".");
  if (t === "") return 0;
  const n = Number(t);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, n);
}

function parseQuantityInput(raw: string): number {
  return parseNonNegativeMoneyInput(raw);
}

function parseMarginPercentInput(raw: string): number {
  const n = parseNonNegativeMoneyInput(raw);
  return Math.min(100, n);
}

type SplitDraftPart = {
  id: string;
  label: string;
  quantityRaw: string;
};

type SplitMode = "rooms" | "manual";

type DragKind = "line" | "section" | "list-edge";

type DragMeta = {
  kind: DragKind;
  rowId: string;
  sectionId: string | null;
  listEdge?: "top" | "bottom";
};

const ESTIMATE_LIST_EDGE_TOP = "estimate-list-edge-top";
const ESTIMATE_LIST_EDGE_BOTTOM = "estimate-list-edge-bottom";

type DropIndicator = {
  rowId: string;
  edge: "top" | "bottom";
};

type SplitPanelState = {
  lineId: string;
  mode: SplitMode;
  roomParts: SplitDraftPart[];
  manualParts: SplitDraftPart[];
  createSectionPerPart: boolean;
};

function roundToOne(value: number): number {
  return Math.round(value * 10) / 10;
}

function defaultManualSplitParts(line: EstimateLine): SplitDraftPart[] {
  const half = line.quantity > 0 ? roundMoney(line.quantity / 2) : 0;
  const second = line.quantity > 0 ? roundMoney(line.quantity - half) : 0;
  return [
    { id: crypto.randomUUID(), label: line.name, quantityRaw: half.toString() },
    { id: crypto.randomUUID(), label: line.name, quantityRaw: second.toString() },
  ];
}

function inferScopeHintsFromLine(line: EstimateLine): ScopeId[] {
  const hints = new Set<ScopeId>();
  const name = line.name.trim().toLowerCase();

  if (line.checklistOfferGroup === "hydraulika") hints.add("hydraulika");
  if (line.checklistOfferGroup === "elektryka") hints.add("elektryka");
  if (line.checklistOfferGroup === "podlogi") {
    hints.add("podloga");
    hints.add("plytki_ceramiczne");
  }
  if (line.checklistOfferGroup === "montaze_naprawy") {
    hints.add("montaze");
    hints.add("naprawy");
  }
  if (line.checklistOfferGroup === "malowanie") {
    hints.add("sciany");
    hints.add("sciany_tylko");
    hints.add("sufit");
  }

  if (/hydraul|wod[- ]?kan|bater|umywalk|wc|toalet/.test(name)) hints.add("hydraulika");
  if (/elektr|gniazd|punkt|opraw|oswietl|oświetl|led|kabel/.test(name)) hints.add("elektryka");
  if (/sufit/.test(name)) hints.add("sufit");
  if (/scian|ścian|malow|grunt|gips|gladz|gładź|tynk/.test(name)) {
    hints.add("sciany");
    hints.add("sciany_tylko");
  }
  if (/podlog|podłog|panel|wylewk|gres|plytk|płytk|terakot/.test(name)) {
    hints.add("podloga");
    hints.add("plytki_ceramiczne");
  }
  if (/montaz|montaż/.test(name)) hints.add("montaze");
  if (/napraw|przygotow/.test(name)) hints.add("naprawy");

  return [...hints];
}

function prefillsFromRooms(line: EstimateLine, rooms: Room[]): SplitDraftPart[] {
  if (!Array.isArray(rooms) || rooms.length === 0) return [];
  const scopeHints = inferScopeHintsFromLine(line);
  const matchedRooms =
    scopeHints.length > 0
      ? rooms.filter((room) => room.scopeIds?.some((scopeId) => scopeHints.includes(scopeId)))
      : [];
  const sourceRooms = matchedRooms.length > 0 ? matchedRooms : rooms;
  if (sourceRooms.length === 0) return [];

  const weights = sourceRooms.map((room) => Math.max(0, room.areaM2));
  const weightSum = weights.reduce((sum, w) => sum + w, 0);
  const effectiveWeights = weightSum > 0 ? weights : sourceRooms.map(() => 1);
  const effectiveWeightSum = effectiveWeights.reduce((sum, w) => sum + w, 0);

  const quantities: number[] = [];
  let assigned = 0;
  for (let i = 0; i < sourceRooms.length; i += 1) {
    if (i === sourceRooms.length - 1) {
      quantities.push(roundToOne(line.quantity - assigned));
      continue;
    }
    const raw = (line.quantity * effectiveWeights[i]) / effectiveWeightSum;
    const rounded = roundToOne(raw);
    quantities.push(rounded);
    assigned = roundToOne(assigned + rounded);
  }

  let sum = roundToOne(quantities.reduce((acc, q) => acc + q, 0));
  const diff = roundToOne(line.quantity - sum);
  if (quantities.length > 0 && Math.abs(diff) > 0) {
    quantities[quantities.length - 1] = roundToOne(quantities[quantities.length - 1] + diff);
    sum = roundToOne(quantities.reduce((acc, q) => acc + q, 0));
  }
  if (Math.abs(sum - line.quantity) > 0.0001 && quantities.length > 0) {
    quantities[quantities.length - 1] = roundMoney(
      quantities[quantities.length - 1] + (line.quantity - sum),
    );
  }

  return sourceRooms.map((room, idx) => ({
    id: crypto.randomUUID(),
    label: room.name,
    quantityRaw: quantities[idx].toString(),
  }));
}

function formatMoneyPln(value: number): string {
  return value.toLocaleString("pl-PL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** Wartość selecta VAT — tylko 8% / 23% / bez; nietypowy ułamek → „bez”, do poprawy ręcznie. */
function lineVatSelectValue(line: EstimateLine): string {
  const r = effectiveLineVatRate(line);
  if (r === 0) return "none";
  if (Math.abs(r - 0.08) < 0.0001) return "8";
  if (Math.abs(r - 0.23) < 0.0001) return "23";
  return "none";
}

type EditField =
  | "name"
  | "quantity"
  | "unit"
  | "labor"
  | "material"
  | "margin"
  | "combinedCost"
  | "unitPrice";

function editKey(lineId: string, field: EditField): string {
  return `${lineId}:${field}`;
}

const EDIT_FIELDS: EditField[] = [
  "name",
  "quantity",
  "unit",
  "labor",
  "material",
  "margin",
  "combinedCost",
  "unitPrice",
];

function parseEditKey(key: string): { lineId: string; field: EditField } | null {
  const i = key.lastIndexOf(":");
  if (i <= 0) return null;
  const lineId = key.slice(0, i);
  const field = key.slice(i + 1) as EditField;
  if (!EDIT_FIELDS.includes(field)) return null;
  return { lineId, field };
}

function sectionSubtotal(rows: OfferRow[], sectionId: string): number {
  const start = rows.findIndex((row) => row.id === sectionId);
  if (start < 0) return 0;
  let sum = 0;
  for (let i = start + 1; i < rows.length; i += 1) {
    const row = rows[i];
    if (isSectionHeader(row)) break;
    sum += lineGrossPrice(row);
  }
  return roundMoney(sum);
}

function sectionIdForRow(rows: OfferRow[], rowId: string): string | null {
  let sectionId: string | null = null;
  for (const row of rows) {
    if (isSectionHeader(row)) {
      if (row.id === rowId) return row.id;
      sectionId = row.id;
    } else if (row.id === rowId) {
      return sectionId;
    }
  }
  return null;
}

function sortableId(kind: DragKind, rowId: string): string {
  return `${kind}:${rowId}`;
}

function parseSortableId(raw: string): { kind: DragKind; rowId: string } | null {
  const [kind, ...rest] = raw.split(":");
  if ((kind !== "line" && kind !== "section") || rest.length === 0) return null;
  return { kind, rowId: rest.join(":") };
}

type FlatDropTarget =
  | { kind: "list-edge"; listEdge: "top" | "bottom" }
  | { kind: "row"; rowId: string; edge: "top" | "bottom" };

/** Przestawia jeden wiersz (sekcja lub pozycja) w płaskiej tablicy `lines`. */
function reorderFlatRows(
  rows: OfferRow[],
  activeRowId: string,
  target: FlatDropTarget,
): OfferRow[] {
  const activeIndex = rows.findIndex((row) => row.id === activeRowId);
  if (activeIndex < 0) return rows;

  const next = [...rows];
  const [item] = next.splice(activeIndex, 1);
  if (!item) return rows;

  let insertIndex: number;
  if (target.kind === "list-edge") {
    insertIndex = target.listEdge === "top" ? 0 : next.length;
  } else {
    if (target.rowId === activeRowId) {
      next.splice(activeIndex, 0, item);
      return rows;
    }
    const overIndex = next.findIndex((row) => row.id === target.rowId);
    if (overIndex < 0) {
      next.splice(activeIndex, 0, item);
      return rows;
    }
    insertIndex = target.edge === "bottom" ? overIndex + 1 : overIndex;
  }

  next.splice(insertIndex, 0, item);
  return next;
}

function ListEdgeDropZone({ edge, colSpan }: { edge: "top" | "bottom"; colSpan: number }) {
  const id = edge === "top" ? ESTIMATE_LIST_EDGE_TOP : ESTIMATE_LIST_EDGE_BOTTOM;
  const { setNodeRef } = useDroppable({
    id,
    data: {
      kind: "list-edge",
      rowId: id,
      sectionId: null,
      listEdge: edge,
    } satisfies DragMeta,
  });

  return (
    <tr className="h-0 border-0" aria-hidden>
      <td colSpan={colSpan} className="relative h-0 border-0 p-0">
        <div
          ref={setNodeRef}
          className={cn(
            "pointer-events-auto absolute left-0 right-0 h-4",
            edge === "top" ? "top-0 -translate-y-1/2" : "bottom-0 translate-y-1/2",
          )}
        />
      </td>
    </tr>
  );
}

function SortableRow({
  id,
  data,
  children,
}: {
  id: string;
  data: DragMeta;
  children: (opts: {
    setNodeRef: (node: HTMLElement | null) => void;
    setActivatorNodeRef: (node: HTMLElement | null) => void;
    attributes: Record<string, unknown>;
    listeners: Record<string, unknown> | undefined;
    transform: { x: number; y: number; scaleX: number; scaleY: number } | null;
    transition: string | undefined;
    isDragging: boolean;
  }) => ReactNode;
}) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } =
    useSortable({
      id,
      data,
    });

  return (
    <>
      {children({
        setNodeRef,
        setActivatorNodeRef,
        attributes,
        listeners,
        transform,
        transition,
        isDragging,
      })}
    </>
  );
}

function hasProofDetails(line: EstimateLine): boolean {
  const t = (s: string | null | undefined) => (s ?? "").trim();
  return Boolean(t(line.assumptionText) || t(line.notes) || t(line.quantityBasis));
}

/** Edytowalne pola — wygląd kontrolki (obramowanie, pointer), nie jak output. */
const EDIT_CELL_BTN = cn(
  "min-h-9 w-full cursor-pointer rounded-md border border-input bg-background px-2 py-1.5 text-sm shadow-sm",
  "transition-[border-color,box-shadow,background-color] duration-100",
  "hover:border-primary/45 hover:bg-muted/20 hover:shadow-md",
  "active:bg-muted/35",
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
);
const EDIT_CELL_BTN_NAME = cn(EDIT_CELL_BTN, "text-left font-medium");
const EDIT_CELL_BTN_NUM = cn(EDIT_CELL_BTN, "text-right font-mono tabular-nums");
const EDIT_CELL_BTN_UNIT = cn(EDIT_CELL_BTN, "text-left font-mono text-xs");

/** Wyliczenia — płaski read-only (lewa kreska zamiast „pill”), bez wrażenia przycisku. */
const CALC_CELL_VAT_AMT = cn(
  "min-h-9 border-l-2 border-l-muted-foreground/30 bg-transparent py-1 pl-2.5 pr-1 text-right font-mono text-sm font-medium tabular-nums text-foreground",
  "select-none"
);

/** Akcent: „Cena jedn. netto” */
const CALC_CELL_UNIT_NET = cn(
  "min-h-9 border-l-[3px] border-l-primary/50 bg-muted/5 py-1 pl-3 pr-1 text-right font-mono text-sm tabular-nums text-foreground",
  "select-none"
);

/** Akcent: „Wartość netto” */
const CALC_CELL_LINE_NET = cn(
  "min-h-9 border-l-[3px] border-l-primary/65 bg-primary/[0.07] py-1.5 pl-3 pr-1 text-right font-mono text-base font-bold tabular-nums text-primary",
  "select-none"
);

/** Akcent: „Wartość brutto” */
const CALC_CELL_GROSS = cn(
  "min-h-9 border-l-[3px] py-1.5 pl-3 pr-1 text-right font-mono text-sm font-semibold tabular-nums text-foreground",
  "select-none"
);

const CALC_CELL_GROSS_STYLE: CSSProperties = {
  borderLeftColor: "color-mix(in srgb, var(--color-success) 60%, transparent)",
  background: "color-mix(in srgb, var(--color-success) 6%, transparent)",
};

const INPUT_EDITABLE = cn(
  "border-input bg-background shadow-sm transition-shadow",
  "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
);

export function MvpEstimateTable({
  lines,
  onChange,
  onAddRow,
  onAddRowFromTemplate,
  loadAddRowTemplates,
  onRestoreRowPrice,
  focusMode = false,
  onFocusModeChange,
  workshopLayout = false,
}: Props) {
  const isMobile = useIsMobile();
  const [isDark, setIsDark] = useState(
    () => document.documentElement.getAttribute("data-theme") === "dark",
  );
  useEffect(() => {
    const obs = new MutationObserver(() =>
      setIsDark(document.documentElement.getAttribute("data-theme") === "dark"),
    );
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
    return () => obs.disconnect();
  }, []);
  const sectionRowBg = isDark ? "#2e2b27" : "hsl(var(--muted))";
  const positionRowBg = isDark ? "#1c1a18" : "hsl(var(--background))";
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [costView, setCostView] = useState<CostTableView>("combined");
  const [addPickerOpen, setAddPickerOpen] = useState(false);
  const [addPickerQuery, setAddPickerQuery] = useState("");
  const [addPickerLoading, setAddPickerLoading] = useState(false);
  const [addPickerError, setAddPickerError] = useState<string | null>(null);
  const [addPickerOptions, setAddPickerOptions] = useState<AddRowTemplateOption[]>([]);
  const [restoreConfirmLineId, setRestoreConfirmLineId] = useState<string | null>(null);
  const [editingSectionId, setEditingSectionId] = useState<string | null>(null);
  const [pendingSectionEditId, setPendingSectionEditId] = useState<string | null>(null);
  const [activeDragMeta, setActiveDragMeta] = useState<DragMeta | null>(null);
  const [dropIndicator, setDropIndicator] = useState<DropIndicator | null>(null);
  const [splitState, setSplitState] = useState<SplitPanelState | null>(null);
  const [draftRooms, setDraftRooms] = useState<Room[]>([]);
  const [mergeSelectedLineIds, setMergeSelectedLineIds] = useState<string[]>([]);
  /** Escape: pomiń commit tylko dla tego samego `editKey` przy blur (bez globalnego „zacięcia”). */
  const pendingCancelEditKeyRef = useRef<string | null>(null);

  const estimateRows = useMemo(() => lines.filter(isEstimateLineRow), [lines]);
  const payableLines = estimateRows.filter((l) => lineGrossPrice(l) > 0);
  const tableTotals = estimateVatSummary(payableLines);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const flatSortableIds = useMemo(
    () =>
      lines.map((row) =>
        sortableId(isSectionHeader(row) ? "section" : "line", row.id),
      ),
    [lines],
  );

  const activeDragRow = useMemo(() => {
    if (!activeDragMeta) return null;
    return lines.find((row) => row.id === activeDragMeta.rowId) ?? null;
  }, [activeDragMeta, lines]);

  const handleDragStart = (event: DragStartEvent) => {
    const meta = event.active.data.current as DragMeta | undefined;
    if (meta?.rowId && meta.kind) {
      setActiveDragMeta(meta);
      return;
    }
    if (typeof event.active.id === "string") {
      const parsed = parseSortableId(event.active.id);
      if (parsed) {
        const sectionId =
          parsed.kind === "section" ? parsed.rowId : sectionIdForRow(lines, parsed.rowId);
        setActiveDragMeta({ kind: parsed.kind, rowId: parsed.rowId, sectionId });
      }
    }
  };

  const handleDragOver = (event: DragOverEvent) => {
    const activeMeta = event.active.data.current as DragMeta | undefined;
    const overMeta = event.over?.data.current as DragMeta | undefined;
    if (!activeMeta || !overMeta || !event.over) {
      setDropIndicator(null);
      return;
    }

    if (overMeta.kind === "list-edge" && overMeta.listEdge) {
      const first = lines[0];
      const last = lines[lines.length - 1];
      if (!first || !last) {
        setDropIndicator(null);
        return;
      }
      setDropIndicator({
        rowId: overMeta.listEdge === "top" ? first.id : last.id,
        edge: overMeta.listEdge,
      });
      return;
    }

    const activeRect =
      event.active.rect.current.translated ?? event.active.rect.current.initial;
    const activeMid = activeRect.top + activeRect.height / 2;
    const overMid = event.over.rect.top + event.over.rect.height / 2;
    setDropIndicator({
      rowId: overMeta.rowId,
      edge: activeMid < overMid ? "top" : "bottom",
    });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const activeMeta = event.active.data.current as DragMeta | undefined;
    const overMeta = event.over?.data.current as DragMeta | undefined;
    const edge =
      dropIndicator &&
      overMeta &&
      (overMeta.kind === "list-edge" || dropIndicator.rowId === overMeta.rowId)
        ? dropIndicator.edge
        : "top";

    setActiveDragMeta(null);
    setDropIndicator(null);

    if (!activeMeta || !overMeta) return;

    let target: FlatDropTarget | null = null;
    if (overMeta.kind === "list-edge" && overMeta.listEdge) {
      target = { kind: "list-edge", listEdge: overMeta.listEdge };
    } else if (overMeta.kind === "section" || overMeta.kind === "line") {
      target = { kind: "row", rowId: overMeta.rowId, edge };
    }
    if (!target) return;

    onChange((prev) => reorderFlatRows(prev, activeMeta.rowId, target));
  };

  useEffect(() => {
    setMergeSelectedLineIds((prev) =>
      prev.filter((id) => estimateRows.some((line) => line.id === id)),
    );
  }, [estimateRows]);

  useEffect(() => {
    if (!pendingSectionEditId) return;
    const exists = lines.some(
      (row) => isSectionHeader(row) && row.id === pendingSectionEditId,
    );
    if (!exists) return;
    setEditingSectionId(pendingSectionEditId);
    setPendingSectionEditId(null);
  }, [lines, pendingSectionEditId]);

  useEffect(() => {
    const draftIdMatch = window.location.pathname.match(/\/mvp\/([^/?#]+)/i);
    const draftId = draftIdMatch?.[1]?.trim();
    if (!draftId) {
      setDraftRooms([]);
      return;
    }
    let cancelled = false;
    const run = async () => {
      const { data, error } = await supabase
        .from("drafts")
        .select("data")
        .eq("id", draftId)
        .single();

      if (cancelled || error || !data) {
        if (!cancelled) setDraftRooms([]);
        return;
      }

      const payload = data as { data?: unknown };
      const rawWizard =
        payload.data && typeof payload.data === "object"
          ? (payload.data as { wizard?: unknown }).wizard
          : undefined;
      if (!rawWizard || typeof rawWizard !== "object") {
        setDraftRooms([]);
        return;
      }

      const normalized = normalizeWizardScopes(
        rawWizard as Parameters<typeof normalizeWizardScopes>[0],
      );
      setDraftRooms(Array.isArray(normalized.rooms) ? normalized.rooms : []);
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, []);

  const updateLine = (id: string, patch: Partial<EstimateLine>) => {
    onChange((prev) =>
      prev.map((row) => (isSectionHeader(row) || row.id !== id ? row : { ...row, ...patch })),
    );
  };

  const setLaborTotal = (line: EstimateLine, totalVal: number) => {
    const q = line.quantity > 0 ? line.quantity : 1;
    updateLine(line.id, { laborUnitPrice: roundMoney(totalVal / q) });
  };

  const setMaterialTotal = (line: EstimateLine, totalVal: number) => {
    const q = line.quantity > 0 ? line.quantity : 1;
    updateLine(line.id, { materialUnitPrice: roundMoney(totalVal / q) });
  };

  const commitField = (key: string, raw: string) => {
    const parsed = parseEditKey(key);
    if (!parsed) return;
    const line = lines.find(
      (l): l is EstimateLine => !isSectionHeader(l) && l.id === parsed.lineId,
    );
    if (!line) return;

    switch (parsed.field) {
      case "name":
        updateLine(line.id, { name: raw.trim() || line.name });
        break;
      case "quantity": {
        const q = parseQuantityInput(raw);
        updateLine(line.id, { quantity: q });
        break;
      }
      case "unit":
        updateLine(line.id, { unit: raw.trim() || line.unit });
        break;
      case "labor": {
        setLaborTotal(line, parseNonNegativeMoneyInput(raw));
        break;
      }
      case "material": {
        setMaterialTotal(line, parseNonNegativeMoneyInput(raw));
        break;
      }
      case "margin": {
        updateLine(line.id, {
          marginPercent: parseMarginPercentInput(raw),
        });
        break;
      }
      case "combinedCost": {
        applyCombinedLaborMaterialTotal(line, parseNonNegativeMoneyInput(raw), updateLine);
        break;
      }
      case "unitPrice": {
        updateLine(line.id, {
          laborUnitPrice: parseNonNegativeMoneyInput(raw),
          materialUnitPrice: 0,
          marginPercent: 0,
        });
        break;
      }
      default:
        break;
    }
  };

  const onInlineKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      e.preventDefault();
      if (editingKey) {
        pendingCancelEditKeyRef.current = editingKey;
      }
      setEditingKey(null);
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      e.currentTarget.blur();
    }
  };

  const onInlineBlur = (key: string, raw: string) => {
    if (pendingCancelEditKeyRef.current === key) {
      pendingCancelEditKeyRef.current = null;
      return;
    }
    commitField(key, raw);
    pendingCancelEditKeyRef.current = null;
    setEditingKey(null);
  };

  const remove = (id: string) => {
    onChange((prev) => prev.filter((l) => l.id !== id));
    setEditingKey(null);
    setEditingSectionId(null);
    setSplitState((prev) => (prev?.lineId === id ? null : prev));
    setMergeSelectedLineIds((prev) => prev.filter((lineId) => lineId !== id));
  };

  const addSection = () => {
    const section: SectionHeader = {
      type: "section",
      id: crypto.randomUUID(),
      title: "Nowa sekcja",
    };
    onChange((prev) => [...prev, section]);
    setPendingSectionEditId(section.id);
  };

  const updateSectionTitle = (sectionId: string, value: string) => {
    const title = value.trim() || "Nowa sekcja";
    onChange((prev) =>
      prev.map((row) =>
        isSectionHeader(row) && row.id === sectionId ? { ...row, title } : row,
      ),
    );
  };

  const removeSection = (sectionId: string) => {
    onChange((prev) => prev.filter((row) => row.id !== sectionId));
    setEditingSectionId((prev) => (prev === sectionId ? null : prev));
  };

  const openSplitPanel = (line: EstimateLine) => {
    const manualParts = defaultManualSplitParts(line);
    const roomParts = prefillsFromRooms(line, draftRooms);
    const hasRoomPrefill = roomParts.length > 0;
    setSplitState({
      lineId: line.id,
      mode: hasRoomPrefill ? "rooms" : "manual",
      roomParts,
      manualParts,
      createSectionPerPart: false,
    });
  };

  const splitActiveLine = useMemo(() => {
    if (!splitState) return null;
    return lines.find(
      (row): row is EstimateLine => !isSectionHeader(row) && row.id === splitState.lineId,
    ) ?? null;
  }, [lines, splitState]);

  const splitParts = useMemo(() => {
    if (!splitState) return [] as SplitDraftPart[];
    return splitState.mode === "rooms" ? splitState.roomParts : splitState.manualParts;
  }, [splitState]);

  const splitSumQuantity = useMemo(() => {
    if (!splitState) return 0;
    return roundMoney(splitParts.reduce((sum, part) => sum + parseQuantityInput(part.quantityRaw), 0));
  }, [splitParts, splitState]);

  const splitValidationError = useMemo(() => {
    if (!splitState || !splitActiveLine) return null;
    if (splitParts.length < 2) return "Dodaj co najmniej 2 sub-wiersze.";
    const namesOk = splitParts.every((part) => part.label.trim().length > 0);
    if (!namesOk) return "Każdy sub-wiersz musi mieć nazwę.";
    const target = splitActiveLine.quantity;
    if (Math.abs(splitSumQuantity - target) > 0.0001) {
      return `Suma ilości musi wynosić ${target}.`;
    }
    return null;
  }, [splitActiveLine, splitParts, splitState, splitSumQuantity]);

  const confirmSplit = () => {
    if (!splitState || !splitActiveLine || splitValidationError) return;
    const parts = splitParts.map((part) => ({
      label: part.label.trim(),
      quantity: parseQuantityInput(part.quantityRaw),
    }));

    onChange((prev) => {
      const idx = prev.findIndex((row) => row.id === splitState.lineId);
      if (idx < 0) return prev;

      const replacementRows: OfferRow[] = [];
      for (const part of parts) {
        const nextLine: EstimateLine = {
          ...splitActiveLine,
          id: crypto.randomUUID(),
          name: part.label || splitActiveLine.name,
          quantity: part.quantity,
        };
        if (splitState.createSectionPerPart) {
          replacementRows.push({
            type: "section",
            id: crypto.randomUUID(),
            title: part.label || splitActiveLine.name,
          });
        }
        replacementRows.push(nextLine);
      }

      const next = [...prev];
      next.splice(idx, 1, ...replacementRows);
      return next;
    });

    setMergeSelectedLineIds((prev) => prev.filter((id) => id !== splitState.lineId));
    setSplitState(null);
  };

  const toggleMergeLine = (lineId: string, checked: boolean) => {
    setMergeSelectedLineIds((prev) => {
      if (checked) {
        if (prev.includes(lineId)) return prev;
        return [...prev, lineId];
      }
      return prev.filter((id) => id !== lineId);
    });
  };

  const mergeSelectedLinesOrdered = useMemo(() => {
    return lines.filter(
      (row): row is EstimateLine => !isSectionHeader(row) && mergeSelectedLineIds.includes(row.id),
    );
  }, [lines, mergeSelectedLineIds]);

  const canMergeSelectedLines = useMemo(() => {
    if (mergeSelectedLinesOrdered.length < 2) return false;
    const first = mergeSelectedLinesOrdered[0];
    const firstUnit = first.unit.trim().toLowerCase();
    const firstCombinedCents = Math.round((first.laborUnitPrice + first.materialUnitPrice) * 100);
    return mergeSelectedLinesOrdered.every((line) => {
      const unitOk = line.unit.trim().toLowerCase() === firstUnit;
      const combinedCents = Math.round((line.laborUnitPrice + line.materialUnitPrice) * 100);
      return unitOk && combinedCents === firstCombinedCents;
    });
  }, [mergeSelectedLinesOrdered]);

  const removeSelectedLines = () => {
    if (mergeSelectedLineIds.length === 0) return;
    const idSet = new Set(mergeSelectedLineIds);
    onChange((prev) => prev.filter((row) => isSectionHeader(row) || !idSet.has(row.id)));
    setMergeSelectedLineIds([]);
  };

  const mergeSelectedLines = () => {
    if (!canMergeSelectedLines || mergeSelectedLinesOrdered.length < 2) return;
    const selectedIdSet = new Set(mergeSelectedLinesOrdered.map((line) => line.id));
    const firstLine = mergeSelectedLinesOrdered[0];
    const mergedQuantity = roundMoney(
      mergeSelectedLinesOrdered.reduce((sum, line) => sum + line.quantity, 0),
    );

    onChange((prev) => {
      const firstIndex = prev.findIndex((row) => row.id === firstLine.id);
      if (firstIndex < 0) return prev;
      const mergedLine: EstimateLine = {
        ...firstLine,
        id: crypto.randomUUID(),
        quantity: mergedQuantity,
        name: firstLine.name,
      };

      const next = prev.filter((row) => isSectionHeader(row) || !selectedIdSet.has(row.id));
      next.splice(firstIndex, 0, mergedLine);
      return next;
    });
    setMergeSelectedLineIds([]);
  };

  useEffect(() => {
    if (!addPickerOpen) return;
    let cancelled = false;
    setAddPickerLoading(true);
    setAddPickerError(null);
    setAddPickerQuery("");

    const run = async () => {
      try {
        const options = await loadAddRowTemplates();
        if (cancelled) return;
        setAddPickerOptions(options);
      } catch (err) {
        if (cancelled) return;
        const message =
          err instanceof Error && err.message ? err.message : "Nie udało się wczytać pozycji.";
        setAddPickerError(message);
        setAddPickerOptions([]);
      } finally {
        if (!cancelled) setAddPickerLoading(false);
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [addPickerOpen, loadAddRowTemplates]);

  const handleSelectTemplate = (template: AddRowTemplateOption) => {
    onAddRowFromTemplate(template);
    setAddPickerOpen(false);
  };

  const handleSelectCustomRow = () => {
    onAddRow();
    setAddPickerOpen(false);
  };

  const pickerContent = (
    <Command
      value={addPickerQuery}
      onValueChange={setAddPickerQuery}
      className="rounded-none border-0 [&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wide"
    >
      <CommandInput placeholder="Szukaj pozycji po nazwie..." />
      <CommandList className="max-h-[min(55vh,24rem)]">
        {addPickerLoading ? (
          <div className="flex items-center gap-2 px-3 py-4 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            Ładowanie pozycji...
          </div>
        ) : null}

        {!addPickerLoading && addPickerError ? (
          <div className="px-3 py-4 text-sm text-destructive">{addPickerError}</div>
        ) : null}

        <CommandGroup heading="Własna pozycja">
          <CommandItem forceMount onSelect={handleSelectCustomRow} value="nowa niestandardowa">
            <Plus className="mr-2 h-4 w-4" aria-hidden />
            Nowa niestandardowa
          </CommandItem>
        </CommandGroup>
        <CommandSeparator />

        {!addPickerLoading && !addPickerError ? (
          <>
            <CommandEmpty>Brak pasujących pozycji.</CommandEmpty>
            {(() => {
              const named = new Map<string, AddRowTemplateOption[]>();
              const uncategorized: AddRowTemplateOption[] = [];
              for (const option of addPickerOptions) {
                const c = option.category?.trim();
                if (!c) uncategorized.push(option);
                else {
                  if (!named.has(c)) named.set(c, []);
                  named.get(c)!.push(option);
                }
              }
              const sortedCategories = [...named.keys()].sort((a, b) => a.localeCompare(b, "pl"));
              const renderItems = (opts: AddRowTemplateOption[]) =>
                opts.map((option) => (
                  <CommandItem
                    key={option.id}
                    value={`${option.name} ${option.unit}`}
                    onSelect={() => handleSelectTemplate(option)}
                    className="items-start py-2"
                  >
                    <div className="flex w-full flex-col gap-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium">{option.name}</span>
                        <Badge variant="outline" className="px-1.5 py-0 font-mono text-[10px]">
                          {option.unit}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Robocizna {formatMoneyPln(option.laborUnitPrice)} zł · Materiał{" "}
                        {formatMoneyPln(option.materialUnitPrice)} zł
                      </p>
                    </div>
                  </CommandItem>
                ));
              return (
                <>
                  {sortedCategories.map((category) => (
                    <CommandGroup key={category} heading={category}>
                      {renderItems(named.get(category)!)}
                    </CommandGroup>
                  ))}
                  {uncategorized.length > 0 ? (
                    <CommandGroup heading="Inne">{renderItems(uncategorized)}</CommandGroup>
                  ) : null}
                </>
              );
            })()}
          </>
        ) : null}
      </CommandList>
    </Command>
  );

  const renderLineActions = (line: EstimateLine) => {
    const hasTemplate =
      typeof (line as EstimateLine & { work_template_id?: string | null }).work_template_id ===
        "string" &&
      Boolean((line as EstimateLine & { work_template_id: string }).work_template_id.trim());

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground"
            aria-label={`Opcje pozycji: ${line.name}`}
          >
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52">
          {onRestoreRowPrice && hasTemplate ? (
            <>
              <DropdownMenuItem onSelect={() => setRestoreConfirmLineId(line.id)}>
                <RotateCcw className="mr-2 h-4 w-4" />
                Przywróć cenę z szablonu
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          ) : null}
          <DropdownMenuItem onSelect={() => openSplitPanel(line)}>
            <Scissors className="mr-2 h-4 w-4" />
            Rozbij
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="text-destructive focus:text-destructive"
            onSelect={() => remove(line.id)}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Usuń wiersz
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  return (
    <div
      className={cn(
        focusMode ? "fixed inset-0 z-50 overflow-auto p-4 space-y-3" : "space-y-3"
      )}
      style={focusMode ? { background: "var(--color-bg-page)" } : undefined}
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <span id="mvp-cost-view-label" className="shrink-0 text-xs font-medium text-muted-foreground">
              Widok tabeli
            </span>
            <ToggleGroup
              type="single"
              value={costView}
              onValueChange={(v) => {
                if (v === "detailed" || v === "combined") setCostView(v);
              }}
              aria-labelledby="mvp-cost-view-label"
              className="inline-flex justify-start rounded-md border border-border/60 p-0.5"
              style={{ background: "var(--color-bg-surface)" }}
            >
              <ToggleGroupItem value="detailed" size="sm" className="px-2.5 text-xs sm:px-3">
                Szczegółowy (zaawansowany)
              </ToggleGroupItem>
              <ToggleGroupItem value="combined" size="sm" className="px-2.5 text-xs sm:px-3">
                Prosty
              </ToggleGroupItem>
            </ToggleGroup>
            {onFocusModeChange ? (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                aria-pressed={focusMode}
                onClick={() => onFocusModeChange(!focusMode)}
                className={cn(
                  "h-9 shrink-0 gap-2 px-3 font-semibold shadow-sm transition-colors",
                  focusMode
                    ? "border border-primary/40 bg-primary/12 text-foreground hover:bg-primary/18"
                    : "border border-border/80 bg-muted/70 text-foreground hover:bg-muted"
                )}
              >
                <Maximize2 className="h-4 w-4 opacity-90" aria-hidden />
                {focusMode ? "Zamknij pełny ekran" : "Pełny ekran"}
              </Button>
            ) : null}
          </div>
          {costView === "combined" ? (
            <p className="text-[11px] text-muted-foreground sm:max-w-[28rem]">
              W widoku prostym edytujesz cenę jednostkową netto wprost; narzut i podział robocizna / materiał są
              ukryte — użyj widoku zaawansowanego, by je zmienić.
            </p>
          ) : null}
        </div>
        <div className="flex w-full shrink-0 flex-wrap items-center gap-2 sm:w-auto sm:justify-end">
          <div
            className={cn(
              "mvp-selection-toolbar flex flex-wrap items-center gap-2",
              mergeSelectedLineIds.length === 0 && "hidden",
            )}
          >
            {canMergeSelectedLines ? (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="h-9 shrink-0"
                onClick={mergeSelectedLines}
              >
                Scal
              </Button>
            ) : null}
            <Button
              type="button"
              variant="destructive"
              size="sm"
              className="h-9 shrink-0"
              onClick={removeSelectedLines}
            >
              Usuń wybrane
            </Button>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setAddPickerOpen(true)}
            className="h-9 shrink-0"
          >
            + Dodaj pozycję
          </Button>
        </div>
      </div>

      {isMobile ? (
        <Drawer open={addPickerOpen} onOpenChange={setAddPickerOpen}>
          <DrawerContent className="max-h-[85vh] p-0">
            <DrawerHeader className="px-4 pb-2 pt-4">
              <DrawerTitle>Dodaj pozycję</DrawerTitle>
              <DrawerDescription>Wybierz pozycję z bazy stawek lub dodaj własną.</DrawerDescription>
            </DrawerHeader>
            {pickerContent}
          </DrawerContent>
        </Drawer>
      ) : (
        <Dialog open={addPickerOpen} onOpenChange={setAddPickerOpen}>
          <DialogContent className="max-w-2xl overflow-hidden p-0">
            <DialogHeader className="border-b px-4 py-3">
              <DialogTitle>Dodaj pozycję</DialogTitle>
              <DialogDescription>Wybierz pozycję z bazy stawek lub dodaj własną.</DialogDescription>
            </DialogHeader>
            {pickerContent}
          </DialogContent>
        </Dialog>
      )}

      {workshopLayout ? (
        <div className="workshop-ai-panel">
          <p className="workshop-ai-label">
            <span className="workshop-ai-dot" aria-hidden />
            Dodaj pozycję
          </p>
          <div className="workshop-ai-tags">
            <button type="button" className="workshop-ai-tag" onClick={onAddRow}>
              + ręcznie
            </button>
            <button type="button" className="workshop-ai-tag" onClick={() => setAddPickerOpen(true)}>
              z biblioteki
            </button>
          </div>
        </div>
      ) : null}

      <div
        className="overflow-hidden rounded-xl border border-border/60"
        style={{ background: "var(--color-bg-surface)" }}
      >
        <div
          className={cn(
            "overflow-auto",
            focusMode ? "max-h-[calc(100vh-8rem)]" : "max-h-[min(55vh,560px)]"
          )}
        >
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={flatSortableIds} strategy={verticalListSortingStrategy}>
          <table
            className={cn(
              "mvp-estimate-table w-full border-collapse text-sm",
              costView === "detailed" ? "min-w-[1240px]" : "min-w-[900px]"
            )}
          >
            <thead
              className="sticky top-0 z-[1] border-b border-border/60 shadow-sm backdrop-blur-sm"
              style={{ background: "var(--color-bg-surface)" }}
            >
              <tr className="text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <th className="w-14 px-1 py-3" />
                <th className="px-3 py-3">Nazwa</th>
                <th className="w-24 px-2 py-3">Ilość</th>
                <th className="w-20 px-2 py-3">Jedn.</th>
                {costView === "detailed" ? (
                  <>
                    <th className="w-24 px-2 py-3 text-right">Robocizna</th>
                    <th className="w-24 px-2 py-3 text-right">Materiał</th>
                    <th className="w-24 px-2 py-3 text-right">Narzut %</th>
                    <th
                      className="w-28 px-2 py-3 text-right"
                      title="Wartość netto pozycji po marży podzielona przez ilość."
                    >
                      Cena jedn. netto
                    </th>
                  </>
                ) : (
                  <th className="w-28 px-2 py-3 text-right" title="Cena jednostkowa netto (edycja).">
                    Cena jedn.
                  </th>
                )}
                <th
                  className="w-32 px-2 py-3 text-right"
                  title="Netto po marży, bez VAT."
                >
                  Wartość netto
                </th>
                <th className="w-[7.25rem] px-2 py-3 text-right">VAT</th>
                <th
                  className="w-32 px-2 py-3 text-right"
                  title="Wartość brutto pozycji (netto + VAT)."
                >
                  Wartość brutto
                </th>
                <th className="w-12 px-1 py-3" />
              </tr>
            </thead>
            <tbody className={cn(mergeSelectedLineIds.length > 0 && "selection-active")}>
              <ListEdgeDropZone edge="top" colSpan={costView === "detailed" ? 12 : 9} />
              {lines.map((row) => {
                if (isSectionHeader(row)) {
                  const subtotal = sectionSubtotal(lines, row.id);
                  const sectionDropTop =
                    dropIndicator?.rowId === row.id && dropIndicator.edge === "top";
                  const sectionDropBottom =
                    dropIndicator?.rowId === row.id && dropIndicator.edge === "bottom";
                  return (
                    <SortableRow
                      key={row.id}
                      id={sortableId("section", row.id)}
                      data={{ kind: "section", rowId: row.id, sectionId: row.id }}
                    >
                      {({
                        attributes,
                        listeners,
                        setNodeRef,
                        setActivatorNodeRef,
                        transform,
                        transition,
                        isDragging,
                      }) => (
                    <tr
                      ref={setNodeRef}
                      className={cn("mvp-estimate-section-row border-b border-border/40")}
                      style={{
                        backgroundColor: sectionRowBg,
                        borderTop: sectionDropTop
                          ? "2px solid color-mix(in srgb, var(--color-accent-primary) 80%, white)"
                          : "1px solid var(--section-row-border)",
                        borderBottom: sectionDropBottom
                          ? "2px solid color-mix(in srgb, var(--color-accent-primary) 80%, white)"
                          : "1px solid var(--section-row-border)",
                        transform: CSS.Transform.toString(transform),
                        transition,
                        opacity: isDragging ? 0.45 : 1,
                      }}
                    >
                      <td
                        className="px-3 py-2 align-middle"
                        style={{
                          backgroundColor: sectionRowBg,
                          color: "var(--section-row-color)",
                          fontSize: "12px",
                          fontWeight: 500,
                        }}
                        colSpan={costView === "detailed" ? 12 : 9}
                      >
                        <div className="group flex items-center justify-between gap-3">
                          <span
                            ref={setActivatorNodeRef}
                            className="drag-handle inline-flex shrink-0 touch-none items-center text-muted-foreground"
                            {...attributes}
                            {...(listeners ?? {})}
                            aria-label={`Przesuń sekcję ${row.title}`}
                          >
                            <GripVertical className="h-4 w-4" />
                          </span>
                          {editingSectionId === row.id ? (
                            <Input
                              autoFocus
                              defaultValue={row.title}
                              className={cn("h-9 max-w-md font-semibold", INPUT_EDITABLE)}
                              onBlur={(event) => {
                                updateSectionTitle(row.id, event.target.value);
                                setEditingSectionId(null);
                              }}
                              onKeyDown={(event) => {
                                if (event.key === "Enter") event.currentTarget.blur();
                                if (event.key === "Escape") setEditingSectionId(null);
                              }}
                            />
                          ) : (
                            <div className="flex items-center gap-1.5">
                              <button
                                type="button"
                                className="text-left text-sm font-semibold text-foreground hover:text-primary"
                                onClick={() => setEditingSectionId(row.id)}
                              >
                                {row.title}
                              </button>
                              <button
                                type="button"
                                className="rounded-sm p-1 text-muted-foreground opacity-0 transition-opacity hover:bg-muted/60 hover:text-foreground group-hover:opacity-100"
                                aria-label={`Edytuj sekcję ${row.title}`}
                                onClick={() => setEditingSectionId(row.id)}
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </button>
                              <button
                                type="button"
                                className="rounded-sm p-1 text-muted-foreground opacity-0 transition-opacity hover:bg-destructive/10 hover:text-destructive group-hover:opacity-100"
                                aria-label={`Usuń sekcję ${row.title}`}
                                onClick={() => removeSection(row.id)}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          )}
                          <span className="font-mono text-sm font-semibold tabular-nums text-foreground">
                            {subtotal.toLocaleString("pl-PL", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}{" "}
                            zł
                          </span>
                        </div>
                      </td>
                    </tr>
                      )}
                    </SortableRow>
                  );
                }

                const line = row;
                const kName = editKey(line.id, "name");
                const kQty = editKey(line.id, "quantity");
                const kUnit = editKey(line.id, "unit");
                const kLabor = editKey(line.id, "labor");
                const kMat = editKey(line.id, "material");
                const kMar = editKey(line.id, "margin");
                const kUnitPrice = editKey(line.id, "unitPrice");
                const lineSectionId = sectionIdForRow(lines, line.id) ?? "__root__";
                const lineDropTop =
                  dropIndicator?.rowId === line.id && dropIndicator.edge === "top";
                const lineDropBottom =
                  dropIndicator?.rowId === line.id && dropIndicator.edge === "bottom";

                return (
                  <Fragment key={line.id}>
                    <SortableRow
                      id={sortableId("line", line.id)}
                      data={{ kind: "line", rowId: line.id, sectionId: lineSectionId }}
                    >
                      {({
                        attributes,
                        listeners,
                        setNodeRef,
                        setActivatorNodeRef,
                        transform,
                        transition,
                        isDragging,
                      }) => (
                  <tr
                    ref={setNodeRef}
                    id={estimateLineElementId(line.id)}
                    className={cn(
                      "mvp-estimate-line-row border-b border-border/40 transition-colors last:border-0 hover:bg-[var(--color-bg-elevated)]",
                    )}
                    style={{
                      backgroundColor: positionRowBg,
                      borderTop: lineDropTop
                        ? "2px solid color-mix(in srgb, var(--color-accent-primary) 80%, white)"
                        : undefined,
                      borderBottom: lineDropBottom
                        ? "2px solid color-mix(in srgb, var(--color-accent-primary) 80%, white)"
                        : undefined,
                      transform: CSS.Transform.toString(transform),
                      transition,
                      opacity: isDragging ? 0.45 : 1,
                    }}
                  >
                    <td className="px-1 py-1.5 align-middle">
                      <div className="flex items-center gap-0.5">
                        <span
                          ref={setActivatorNodeRef}
                          className="drag-handle inline-flex shrink-0 touch-none text-muted-foreground"
                          {...attributes}
                          {...(listeners ?? {})}
                          aria-hidden
                        >
                          <GripVertical className="h-4 w-4" />
                        </span>
                        <Checkbox
                          className="row-checkbox"
                          aria-label={`Wybierz pozycję do scalenia: ${line.name}`}
                          checked={mergeSelectedLineIds.includes(line.id)}
                          onCheckedChange={(checked) => toggleMergeLine(line.id, checked === true)}
                        />
                      </div>
                    </td>
                    <td className="px-2 py-1.5 align-middle">
                      {editingKey === kName ? (
                        <Input
                          key={kName}
                          autoFocus
                          defaultValue={line.name}
                          className={cn("h-9 min-w-[140px] font-medium", INPUT_EDITABLE)}
                          onBlur={(e) => onInlineBlur(kName, e.target.value)}
                          onKeyDown={onInlineKeyDown}
                        />
                      ) : (
                        <button
                          type="button"
                          onClick={() => setEditingKey(kName)}
                          className={EDIT_CELL_BTN_NAME}
                        >
                          <span className="inline-flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                            <span>{line.name}</span>
                            {line.source === "generator" ? (
                              <span className="text-[10px] font-normal uppercase tracking-wide text-muted-foreground">
                                z generatora
                              </span>
                            ) : null}
                          </span>
                        </button>
                      )}
                      {hasProofDetails(line) ? (
                        <Collapsible className="mt-1.5 w-full">
                          <CollapsibleTrigger className="flex w-full items-center gap-1 rounded-md px-1 py-0.5 text-left text-[11px] text-muted-foreground hover:bg-[var(--color-bg-elevated)] hover:text-foreground [&[data-state=open]>svg]:rotate-180">
                            <ChevronDown className="h-3.5 w-3.5 shrink-0 transition-transform duration-200" />
                            Założenia i uzasadnienie
                          </CollapsibleTrigger>
                          <CollapsibleContent
                            className="mt-1.5 space-y-2 rounded-md border border-border/50 px-2 py-2 text-xs text-muted-foreground"
                            style={{ background: "var(--color-bg-surface)" }}
                          >
                            {(line.quantityBasis ?? "").trim() ? (
                              <div>
                                <span className="font-medium text-foreground">Skąd ta ilość?</span>
                                <p className="mt-0.5 whitespace-pre-wrap">{line.quantityBasis}</p>
                              </div>
                            ) : null}
                          </CollapsibleContent>
                        </Collapsible>
                      ) : null}
                    </td>
                    <td className="px-2 py-1.5 align-middle">
                      {editingKey === kQty ? (
                        <Input
                          key={kQty}
                          type="number"
                          inputMode="decimal"
                          min={0}
                          step={0.01}
                          autoFocus
                          defaultValue={line.quantity}
                          className={cn("h-9 font-mono tabular-nums", INPUT_EDITABLE)}
                          onBlur={(e) => onInlineBlur(kQty, e.target.value)}
                          onKeyDown={onInlineKeyDown}
                        />
                      ) : (
                        <button
                          type="button"
                          onClick={() => setEditingKey(kQty)}
                          className={EDIT_CELL_BTN_NUM}
                        >
                          {line.quantity}
                        </button>
                      )}
                    </td>
                    <td className="px-2 py-1.5 align-middle">
                      {editingKey === kUnit ? (
                        <Input
                          key={kUnit}
                          autoFocus
                          defaultValue={line.unit}
                          className={cn("h-9 font-mono text-xs", INPUT_EDITABLE)}
                          onBlur={(e) => onInlineBlur(kUnit, e.target.value)}
                          onKeyDown={onInlineKeyDown}
                        />
                      ) : (
                        <button
                          type="button"
                          onClick={() => setEditingKey(kUnit)}
                          className={EDIT_CELL_BTN_UNIT}
                        >
                          {line.unit}
                        </button>
                      )}
                    </td>
                    {costView === "detailed" ? (
                      <>
                        <td className="px-2 py-1.5 align-middle">
                          {editingKey === kLabor ? (
                            <Input
                              key={kLabor}
                              type="number"
                              inputMode="decimal"
                              min={0}
                              step={0.01}
                              autoFocus
                              defaultValue={laborTotal(line)}
                              className={cn("h-9 text-right font-mono tabular-nums", INPUT_EDITABLE)}
                              onBlur={(e) => onInlineBlur(kLabor, e.target.value)}
                              onKeyDown={onInlineKeyDown}
                            />
                          ) : (
                            <button
                              type="button"
                              onClick={() => setEditingKey(kLabor)}
                              className={EDIT_CELL_BTN_NUM}
                            >
                              {laborTotal(line).toLocaleString("pl-PL", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}{" "}
                              zł
                            </button>
                          )}
                          <div className="px-2 text-[10px] text-muted-foreground">suma PLN</div>
                        </td>
                        <td className="px-2 py-1.5 align-middle">
                          {editingKey === kMat ? (
                            <Input
                              key={kMat}
                              type="number"
                              inputMode="decimal"
                              min={0}
                              step={0.01}
                              autoFocus
                              defaultValue={materialTotal(line)}
                              className={cn("h-9 text-right font-mono tabular-nums", INPUT_EDITABLE)}
                              onBlur={(e) => onInlineBlur(kMat, e.target.value)}
                              onKeyDown={onInlineKeyDown}
                            />
                          ) : (
                            <button
                              type="button"
                              onClick={() => setEditingKey(kMat)}
                              className={EDIT_CELL_BTN_NUM}
                            >
                              {materialTotal(line).toLocaleString("pl-PL", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}{" "}
                              zł
                            </button>
                          )}
                          <div className="px-2 text-[10px] text-muted-foreground">suma PLN</div>
                        </td>
                      </>
                    ) : null}
                    {costView === "detailed" ? (
                      <td className="px-2 py-1.5 align-middle">
                        {editingKey === kMar ? (
                          <Input
                            key={kMar}
                            type="number"
                            inputMode="decimal"
                            min={0}
                            max={100}
                            step={0.5}
                            autoFocus
                            defaultValue={line.marginPercent}
                            className={cn("h-9 text-right font-mono tabular-nums", INPUT_EDITABLE)}
                            onBlur={(e) => onInlineBlur(kMar, e.target.value)}
                            onKeyDown={onInlineKeyDown}
                          />
                        ) : (
                          <button
                            type="button"
                            onClick={() => setEditingKey(kMar)}
                            className={EDIT_CELL_BTN_NUM}
                          >
                            {line.marginPercent}%
                          </button>
                        )}
                      </td>
                    ) : null}
                    {costView === "detailed" ? (
                      <td className="px-2 py-1.5 align-middle text-right">
                        <div className={CALC_CELL_UNIT_NET}>
                          {line.quantity > 0
                            ? `${lineUnitNetAfterMargin(line).toLocaleString("pl-PL", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })} zł`
                            : "—"}
                        </div>
                        <div className="mt-0.5 px-2 text-[10px] text-muted-foreground">po marży</div>
                      </td>
                    ) : (
                      <td className="px-2 py-1.5 align-middle text-right">
                        {editingKey === kUnitPrice ? (
                          <Input
                            key={kUnitPrice}
                            type="number"
                            inputMode="decimal"
                            min={0}
                            step={0.01}
                            autoFocus
                            defaultValue={lineUnitNetAfterMargin(line)}
                            className={cn("h-9 text-right font-mono tabular-nums", INPUT_EDITABLE)}
                            onBlur={(e) => onInlineBlur(kUnitPrice, e.target.value)}
                            onKeyDown={onInlineKeyDown}
                          />
                        ) : (
                          <button
                            type="button"
                            onClick={() => setEditingKey(kUnitPrice)}
                            className={EDIT_CELL_BTN_NUM}
                          >
                            {lineUnitNetAfterMargin(line) === 0
                              ? "0,00 zł"
                              : `${lineUnitNetAfterMargin(line).toLocaleString("pl-PL", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })} zł`}
                          </button>
                        )}
                      </td>
                    )}
                    <td className="px-2 py-1.5 align-middle text-right">
                      <div className={CALC_CELL_LINE_NET}>
                        {lineFinalPrice(line).toLocaleString("pl-PL", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}{" "}
                        zł
                      </div>
                      {costView === "detailed" ? (
                        <div className="mt-0.5 text-[10px] font-normal text-muted-foreground">
                          marża {lineMarginAmount(line).toFixed(2)} zł
                        </div>
                      ) : null}
                    </td>
                    <td className="px-2 py-1.5 align-middle text-right">
                      <div className={CALC_CELL_VAT_AMT}>
                        {lineVatAmount(line).toLocaleString("pl-PL", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}{" "}
                        zł
                      </div>
                      <div className="mt-1.5 flex justify-end">
                        <Select
                          value={lineVatSelectValue(line)}
                          onValueChange={(v) =>
                            updateLine(line.id, { vatRate: vatRateFromMode(v as VatMode) })
                          }
                        >
                          <SelectTrigger
                            className={cn(
                              "h-8 w-full min-w-[4.75rem] max-w-[6.25rem] cursor-pointer px-2 text-[11px] font-mono tabular-nums",
                              "border border-input shadow-sm transition-colors hover:border-primary/40 hover:bg-[var(--color-bg-elevated)]"
                            )}
                            style={{ background: "var(--color-bg-surface)" }}
                            aria-label={`Stawka VAT — ${line.name}`}
                            title={
                              (() => {
                                const r = effectiveLineVatRate(line);
                                const std =
                                  r === 0 ||
                                  Math.abs(r - 0.08) < 0.0001 ||
                                  Math.abs(r - 0.23) < 0.0001;
                                return std
                                  ? "Stawka VAT dla pozycji"
                                  : `Niestandardowa stawka ${(r * 100).toFixed(2)}% — wybierz stawkę z listy`;
                              })()
                            }
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Bez VAT</SelectItem>
                            <SelectItem value="8">8%</SelectItem>
                            <SelectItem value="23">23%</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </td>
                    <td className="px-2 py-1.5 align-middle text-right">
                      <div className={CALC_CELL_GROSS} style={CALC_CELL_GROSS_STYLE}>
                        {lineGrossPrice(line).toLocaleString("pl-PL", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}{" "}
                        zł
                      </div>
                    </td>
                    <td className="px-1 py-1.5 align-middle">
                      {renderLineActions(line)}
                    </td>
                  </tr>
                      )}
                    </SortableRow>
                  {splitState?.lineId === line.id ? (
                    <tr className="border-b border-border/40 bg-muted/15">
                      <td colSpan={costView === "detailed" ? 12 : 9} className="px-3 py-3">
                        <div className="space-y-3 rounded-lg border border-border/60 bg-background/75 p-3">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <p className="text-sm font-semibold text-foreground">
                              Rozbij pozycję: {line.name}
                            </p>
                            <Button type="button" size="sm" variant="ghost" onClick={() => setSplitState(null)}>
                              Zamknij
                            </Button>
                          </div>

                          <div className="space-y-2">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                                Tryb podziału
                              </span>
                              <ToggleGroup
                                type="single"
                                value={splitState.mode}
                                onValueChange={(value) => {
                                  if (value !== "rooms" && value !== "manual") return;
                                  setSplitState((prev) =>
                                    prev && prev.lineId === line.id
                                      ? { ...prev, mode: value as SplitMode }
                                      : prev,
                                  );
                                }}
                                className="rounded-md border border-border/60 p-0.5"
                              >
                                <ToggleGroupItem
                                  value="rooms"
                                  size="sm"
                                  className="px-2.5 text-xs"
                                  disabled={splitState.roomParts.length === 0}
                                >
                                  Z pomieszczeń
                                </ToggleGroupItem>
                                <ToggleGroupItem value="manual" size="sm" className="px-2.5 text-xs">
                                  Ręcznie
                                </ToggleGroupItem>
                              </ToggleGroup>
                            </div>
                            {splitParts.map((part, idx) => {
                              const q = parseQuantityInput(part.quantityRaw);
                              const autoValue = roundMoney(q * lineUnitNetAfterMargin(line));
                              return (
                                <div key={part.id} className="grid gap-2 sm:grid-cols-[1fr_7.5rem_9rem_auto] sm:items-center">
                                  <Input
                                    value={part.label}
                                    onChange={(event) =>
                                      setSplitState((prev) =>
                                        prev && prev.lineId === line.id
                                          ? {
                                              ...prev,
                                              [prev.mode === "rooms" ? "roomParts" : "manualParts"]: (
                                                prev.mode === "rooms" ? prev.roomParts : prev.manualParts
                                              ).map((item) =>
                                                item.id === part.id ? { ...item, label: event.target.value } : item,
                                              ),
                                            }
                                          : prev,
                                      )
                                    }
                                    placeholder={`Nazwa / pomieszczenie ${idx + 1}`}
                                    className="h-9"
                                  />
                                  <Input
                                    value={part.quantityRaw}
                                    onChange={(event) =>
                                      setSplitState((prev) =>
                                        prev && prev.lineId === line.id
                                          ? {
                                              ...prev,
                                              [prev.mode === "rooms" ? "roomParts" : "manualParts"]: (
                                                prev.mode === "rooms" ? prev.roomParts : prev.manualParts
                                              ).map((item) =>
                                                item.id === part.id
                                                  ? { ...item, quantityRaw: event.target.value }
                                                  : item,
                                              ),
                                            }
                                          : prev,
                                      )
                                    }
                                    type="number"
                                    inputMode="decimal"
                                    min={0}
                                    step={0.01}
                                    className="h-9 text-right font-mono tabular-nums"
                                  />
                                  <div className="text-right font-mono text-sm tabular-nums text-muted-foreground">
                                    {autoValue.toLocaleString("pl-PL", {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    })}{" "}
                                    zł
                                  </div>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() =>
                                      setSplitState((prev) =>
                                        prev &&
                                        prev.lineId === line.id &&
                                        (prev.mode === "rooms" ? prev.roomParts.length : prev.manualParts.length) > 2
                                          ? {
                                              ...prev,
                                              [prev.mode === "rooms" ? "roomParts" : "manualParts"]: (
                                                prev.mode === "rooms" ? prev.roomParts : prev.manualParts
                                              ).filter((item) => item.id !== part.id),
                                            }
                                          : prev,
                                      )
                                    }
                                    disabled={splitParts.length <= 2}
                                  >
                                    Usuń
                                  </Button>
                                </div>
                              );
                            })}
                          </div>

                          <div className="flex flex-wrap items-center justify-between gap-3">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                setSplitState((prev) =>
                                  prev && prev.lineId === line.id
                                    ? {
                                        ...prev,
                                        [prev.mode === "rooms" ? "roomParts" : "manualParts"]: [
                                          ...(prev.mode === "rooms" ? prev.roomParts : prev.manualParts),
                                          {
                                            id: crypto.randomUUID(),
                                            label: line.name,
                                            quantityRaw: "0",
                                          },
                                        ],
                                      }
                                    : prev,
                                )
                              }
                            >
                              <Plus className="mr-2 h-4 w-4" />
                              Dodaj sub-wiersz
                            </Button>
                            <div className="flex items-center gap-2">
                              <Checkbox
                                id={`split-create-sections-${line.id}`}
                                checked={splitState.createSectionPerPart}
                                onCheckedChange={(checked) =>
                                  setSplitState((prev) =>
                                    prev && prev.lineId === line.id
                                      ? { ...prev, createSectionPerPart: checked === true }
                                      : prev,
                                  )
                                }
                              />
                              <label
                                htmlFor={`split-create-sections-${line.id}`}
                                className="text-sm text-foreground"
                              >
                                Utwórz sekcje dla każdego wiersza
                              </label>
                            </div>
                          </div>

                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <p className="text-sm text-muted-foreground">
                              Suma ilości:{" "}
                              <span className="font-mono tabular-nums">
                                {splitSumQuantity.toLocaleString("pl-PL", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                              </span>{" "}
                              / {line.quantity}
                            </p>
                            <Button
                              type="button"
                              size="sm"
                              onClick={confirmSplit}
                              disabled={splitValidationError != null}
                            >
                              Zatwierdź rozbicie
                            </Button>
                          </div>

                          {splitValidationError ? (
                            <p className="text-sm text-destructive">{splitValidationError}</p>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ) : null}
                  </Fragment>
                );
              })}
              <ListEdgeDropZone edge="bottom" colSpan={costView === "detailed" ? 12 : 9} />
            </tbody>
          </table>
            </SortableContext>
            <DragOverlay>
              {activeDragRow ? (
                <div className="rounded-md border border-border/70 bg-card px-3 py-2 shadow-xl">
                  {isSectionHeader(activeDragRow) ? (
                    <div className="text-sm font-semibold">{activeDragRow.title || "Sekcja"}</div>
                  ) : (
                    <div className="space-y-0.5">
                      <div className="text-sm font-semibold">{activeDragRow.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {activeDragRow.quantity} {activeDragRow.unit}
                      </div>
                    </div>
                  )}
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        </div>

        <div
          className="sticky bottom-0 z-[2] flex flex-wrap items-end justify-between gap-4 border-t border-border/60 px-3 py-3 shadow-[0_-6px_16px_-4px_rgba(0,0,0,0.08)] sm:px-4"
          style={{ background: "var(--color-bg-surface)" }}
          role="status"
        >
          <div className="flex flex-1 flex-wrap items-end justify-end gap-5 sm:gap-8">
            <div className="text-right">
              <span className="block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                Suma netto
              </span>
              <span className="font-mono text-lg font-bold tabular-nums text-primary">
                {tableTotals.netTotal.toLocaleString("pl-PL", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}{" "}
                zł
              </span>
            </div>
            <div className="text-right">
              <span className="block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                Suma VAT
              </span>
              <span className="font-mono text-lg font-bold tabular-nums text-foreground">
                {tableTotals.vatAmount.toLocaleString("pl-PL", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}{" "}
                zł
              </span>
            </div>
            <div className="text-right">
              <span className="block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                Razem brutto
              </span>
              <span className="font-mono text-xl font-bold tabular-nums text-foreground">
                {tableTotals.grossTotal.toLocaleString("pl-PL", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}{" "}
                zł
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <Button type="button" variant="outline" size="sm" className="h-9" onClick={addSection}>
          + Dodaj sekcję
        </Button>
      </div>

      <AlertDialog
        open={restoreConfirmLineId !== null}
        onOpenChange={(open) => {
          if (!open) setRestoreConfirmLineId(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Przywrócić cenę z bazy?</AlertDialogTitle>
            <AlertDialogDescription>
              Cena tej pozycji zostanie zastąpiona stawką z Twoich szablonów.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Anuluj</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (restoreConfirmLineId == null) return;
                onRestoreRowPrice?.(restoreConfirmLineId);
                setRestoreConfirmLineId(null);
              }}
            >
              Przywróć
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {estimateRows.length === 0 && (
        <p className="text-sm text-muted-foreground">
          Brak pozycji — użyj „Dodaj pozycję” albo wróć do kreatora.
        </p>
      )}
    </div>
  );
}
